import type { MedicationOrderContext } from '../types/medicationAI'

const JSON_INSTRUCTION = `Return ONLY valid JSON (no markdown) with this shape:
{
  "explanations": [ { "alertId": string, "briefSummary": string, "clinicalDetail": string (optional) } ],
  "rankedAlternatives": [ { "referenceDrugId": string, "rationale": string } ],
  "educationalNotes": string (optional)
}
Rules:
- For explanations, only comment on alerts listed in systemAlerts; use each alert's "id" as alertId.
- For rankedAlternatives, ONLY use referenceDrugId values from the formularyAlternatives list. Do not invent drug IDs or names not in that list.
- This is adjunct information only; the prescriber must rely on authoritative clinical systems for safety decisions.`

export function buildMedicationOrderPrompt(context: MedicationOrderContext): string {
  const parts: string[] = []
  parts.push('You assist a licensed prescriber with adjunct explanations for medication ordering.')
  parts.push(JSON_INSTRUCTION)
  parts.push('')
  if (context.patientAge != null && context.patientGender) {
    parts.push(`Patient: ${context.patientAge}-year-old ${context.patientGender}`)
  }
  if (context.chronicConditions?.length) {
    parts.push(`Chronic conditions: ${context.chronicConditions.join(', ')}`)
  }
  if (context.allergies?.length) {
    parts.push(`Allergies: ${context.allergies.join(', ')}`)
  }
  if (context.currentMedications?.length) {
    parts.push(`Current medications: ${context.currentMedications.join(', ')}`)
  }
  parts.push('')
  parts.push('Proposed prescription lines:')
  context.proposedMedications.forEach((m, i) => {
    parts.push(
      `  ${i + 1}. ${m.name}${m.dose ? ` — ${m.dose}` : ''}${m.indication ? ` — indication: ${m.indication}` : ''}`,
    )
  })
  parts.push('')
  if (context.systemAlerts?.length) {
    parts.push('System alerts (authoritative CDS — explain these only, do not contradict):')
    context.systemAlerts.forEach((a) => {
      parts.push(`  - id=${a.id} kind=${a.kind} severity=${a.severity || 'n/a'}: ${a.summary}`)
    })
    parts.push('')
  }
  if (context.formularyAlternatives?.length) {
    parts.push('Allowed formulary alternatives (only rank among these by referenceDrugId):')
    context.formularyAlternatives.forEach((f) => {
      parts.push(`  - ${f.referenceDrugId}: ${f.productName}`)
    })
    parts.push('')
  }
  return parts.join('\n')
}
